// ============================================================================
// STYLES
// ============================================================================
import '../stylesheets/application.css'

// Note: FullCalendar v6 doesn't ship with separate CSS files
// All styling is handled in our custom calendar.css

// ============================================================================
// CORE LIBRARIES
// ============================================================================
// Turbo for SPA-like navigation
import * as Turbo from '@hotwired/turbo'
Turbo.start()

// Stimulus for JavaScript behavior
import { Application } from '@hotwired/stimulus'

// Flowbite with Turbo support for UI components
import "flowbite/dist/flowbite.turbo.js"

// Flowbite Datepicker for advanced date picker features
import { Datepicker } from 'flowbite-datepicker'
window.Datepicker = Datepicker

// ============================================================================
// THIRD-PARTY PLUGINS
// ============================================================================
// ApexCharts for data visualization
import ApexCharts from 'apexcharts'
window.ApexCharts = ApexCharts

// Pagy for pagination
import Pagy from '../lib/pagy.mjs'

// ============================================================================
// CUSTOM MANAGERS
// ============================================================================
import { DatepickerManager } from '../lib/datepicker_manager'
import { Sidebar } from '../lib/sidebar'
import '../lib/flash_messages'
import { setupDashboard } from '../lib/dashboard_filters'

// ============================================================================
// STIMULUS SETUP
// ============================================================================
const application = Application.start()

// Auto-register all controllers
const controllers = import.meta.glob('../controllers/**/*_controller.js', { eager: true })
Object.entries(controllers).forEach(([path, module]) => {
  const name = path
    .replace('../controllers/', '')
    .replace('_controller.js', '')
    .replace(/_/g, '-')
  
  application.register(name, module.default)
})

// ============================================================================
// APPLICATION INITIALIZATION
// ============================================================================

class ApplicationInitializer {
  static initialize() {
    // Initialize all UI components
    DatepickerManager.initialize()
    Sidebar.initialize()
    Pagy.init()
    setupDashboard()
  }
  
  static initializeForTurboLoad() {
    // Full initialization including Pagy for turbo:load events
    DatepickerManager.initialize()
    Sidebar.initialize()
    Pagy.init()
  }

  static initializeForTurboRender() {
    // Initialize components that are safe for multiple calls
    // Exclude Pagy to avoid conflicts with frame-specific initialization
    DatepickerManager.initialize()
    Sidebar.initialize()
  }

  static initializePagyForFrame(frame) {
    // Initialize Pagy specifically within a Turbo Frame
    Pagy.init(frame)
  }
}

// ============================================================================
// GLOBAL FUNCTIONS
// ============================================================================
// Right-click dropdown functionality for table rows
window.showRowDropdown = function(event, id) {
  event.preventDefault() // Prevent default context menu
  
  const dropdownButton = document.getElementById(`dropdownMenuButton${id}`)
  if (dropdownButton) {
    // Simulate click on the dropdown button to show the menu
    dropdownButton.click()
  }
}

// Timer modal functions
window.hideStopModal = function(event) {
  if (event && event.target !== event.currentTarget) return // Only handle background clicks
  
  const modal = document.getElementById('stopTimerModal')
  if (modal) {
    modal.classList.add('hidden')
  }
}

window.submitStopTimer = function(form) {
  // Hide modal immediately 
  window.hideStopModal()
  // Form will submit naturally via Turbo
  return true
}

// ============================================================================
// EVENT LISTENERS
// ============================================================================
// Initial page load
document.addEventListener('DOMContentLoaded', () => {
  ApplicationInitializer.initialize()
})

// Turbo navigation events - turbo:load includes Pagy initialization
document.addEventListener('turbo:load', () => {
  ApplicationInitializer.initializeForTurboLoad()
})

// Turbo render events - excludes Pagy to avoid conflicts
document.addEventListener('turbo:render', () => {
  ApplicationInitializer.initializeForTurboRender()
})

// Handle Turbo Frame updates (specifically for pagination within frames)
document.addEventListener('turbo:frame-render', (event) => {
  const frame = event.target
  
  // Only reinitialize Pagy for frames that might contain pagination
  if (frame.id === 'time_logs_content') {
    ApplicationInitializer.initializePagyForFrame(frame)
  }
})